import { Logger, Presenter } from 'wdc-cube'

import { type EmpresaFilter } from '../the-consumer-market/tcm_service'

import { TheConnectionKeys } from './tc_keys'
import { type TheConnectionPresenter } from './tc_presenter'
import { ListagemTabsScope, type TcListagemTabsOption } from './tc_scopes'
import TcPresenterForEmpresasListagem from './tc_presenter_listagem_empresas'
import TcPresenterForSociosListagem from './tc_presenter_listagem_socios'

const LOG = Logger.get('TcPresenterListagemTabs')

export default class TcPresenterListagemTabs extends Presenter<ListagemTabsScope, TheConnectionPresenter> {
    constructor(owner: TheConnectionPresenter) {
        super(owner, owner.scope.listagemTabs)
        this.__parent = owner

        this.empresasListagem = new TcPresenterForEmpresasListagem(owner)
        this.sociosListagem = new TcPresenterForSociosListagem(owner)
    }

    private readonly __parent: TheConnectionPresenter

    readonly empresasListagem: TcPresenterForEmpresasListagem
    readonly sociosListagem: TcPresenterForSociosListagem

    private __loadedTabs = new Set<TcListagemTabsOption>()

    override release(): void {
        this.empresasListagem.release()
        this.sociosListagem.release()
        super.release()
        LOG.debug('release')
    }

    async initialize(keys: TheConnectionKeys) {
        this.scope.active = toListagemTabsOption(keys.selectedListingTab)
        this.scope.onSelect = this.action(this.__handleTabSelected)
        this.scope.onExpandClick = this.action(this.__handleExpandClick)
        this.scope.onShrunkClick = this.action(this.__handleShrunkClick)

        await this.empresasListagem.initialize()
        await this.sociosListagem.initialize()
        LOG.debug('Initialized')
    }

    async updateFromIntent(keys: TheConnectionKeys) {
        const newActiveTab = toListagemTabsOption(keys.selectedListingTab)
        if (this.scope.active !== newActiveTab) {
            await this.__handleTabSelected(newActiveTab)
        }
    }

    publishParameters(keys: TheConnectionKeys): void {
        keys.selectedListingTab = this.scope.active
    }

    clear() {
        this.__loadedTabs.clear()
        this.empresasListagem.clear()
        this.sociosListagem.clear()
    }

    invalidate() {
        this.__loadedTabs.clear()
    }

    async fetchActiveTab(filter?: EmpresaFilter) {
        if (this.__parent.graphCompanyIds.length === 0) {
            this.clear()
            return
        }

        const tab = this.scope.active
        if (this.__loadedTabs.has(tab)) {
            return
        }
        this.__loadedTabs.add(tab)
        await this.__fetchTab(tab, filter)
    }

    private async __fetchTab(tab: TcListagemTabsOption, filter?: EmpresaFilter) {
        const tableFilter = filter ?? this.__parent.buildTableFilter()
        try {
            if (tab === 'qsa') {
                await this.sociosListagem.fetch(tableFilter)
            } else {
                await this.empresasListagem.fetch(tableFilter)
            }
        } catch (caught) {
            this.__loadedTabs.delete(tab)
            throw caught
        }
    }

    private async __handleExpandClick() {
        await this.__parent.setTablesExpanded(true)
    }

    private async __handleShrunkClick() {
        await this.__parent.setTablesExpanded(false)
    }

    private async __handleTabSelected(tab: TcListagemTabsOption) {
        this.scope.active = tab
        this.__parent.updateHistory()
        await this.fetchActiveTab()
    }
}

function toListagemTabsOption(v: string | undefined | null): TcListagemTabsOption {
    return v === 'qsa' ? 'qsa' : 'pj'
}
